import React, { useState } from "react";
import { toast } from "sonner";
import PageHero from "@/components/site/PageHero";
import { Reveal, SectionHeading, PillButton } from "@/components/primitives";
import { Icon } from "@/lib/icons";
import { useSeo } from "@/lib/useSeo";
import { CAREER_CATEGORIES, OPEN_POSITIONS, COMPANY } from "@/lib/content";
import { CAREER_BENEFITS, HIRING_PROCESS, LIFE_AT_VARDHIX } from "@/lib/contentV2";
import { submitCareer } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CULTURE = [
  { icon: "Rocket", title: "Ownership", desc: "Small teams, real responsibility, and the freedom to ship." },
  { icon: "Users", title: "People First", desc: "We invest in growth, mentorship and well-being." },
  { icon: "Cpu", title: "Craft & Speed", desc: "High engineering standards with fast iteration." },
  { icon: "ShieldCheck", title: "Integrity", desc: "Responsible, transparent and user-focused." },
];

const initial = { full_name: "", email: "", phone: "", role: "", experience: "", linkedin: "", portfolio: "", message: "", website: "" };

export default function Careers() {
  useSeo({
    title: "Careers",
    description:
      "Build products that can impact millions. Explore careers at Vardhix Technologies across engineering, product, data, risk and more — based in Pune, remote & hybrid friendly.",
    breadcrumb: [{ name: "Home", path: "/" }, { name: "Careers", path: "/careers" }],
  });

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.role) {
      toast.error("Please fill your name, email and preferred role.");
      return;
    }
    setLoading(true);
    try {
      await submitCareer(form);
      toast.success("Application received! Our team will be in touch.");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        overline="Careers"
        title="Build Products That Can Impact Millions"
        sub="Join a fast-growing Indian technology company building fintech infrastructure, trading evaluation systems and scalable digital platforms. Pune-based, remote & hybrid friendly."
      />

      {/* Categories */}
      <section className="relative py-16">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading overline="Teams" title="Where you can make an impact" />
          <div className="mt-10 flex flex-wrap gap-3">
            {CAREER_CATEGORIES.map((c) => (
              <Reveal key={c}>
                <span className="rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-sm text-slate-200 hover:border-teal/40 hover:text-teal transition-colors">{c}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Open positions */}
      <section className="relative py-16 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading overline="Open Positions" title="Current openings" />
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {OPEN_POSITIONS.map((p, i) => (
              <Reveal key={p.title} delay={(i % 2) * 0.06}>
                <div className="group glass rounded-2xl p-6 flex items-center justify-between gap-4 hover:border-teal/40 transition-colors" data-testid={`position-${i}`}>
                  <div>
                    <h3 className="font-heading font-semibold text-white">{p.title}</h3>
                    <p className="mt-1 text-sm text-slate-400">{p.dept} &middot; {p.location} &middot; {p.type}</p>
                  </div>
                  <a href="#apply" className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-teal/40 text-teal group-hover:bg-teal group-hover:text-navy transition-colors">
                    <Icon name="ArrowUpRight" className="h-4 w-4" />
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Culture */}
      <section className="relative py-16 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading overline="Work Culture" title="How we work" />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CULTURE.map((c, i) => (
              <Reveal key={c.title} delay={(i % 4) * 0.06}>
                <div className="glass rounded-2xl p-6 h-full">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-teal/10 border border-teal/30 text-teal">
                    <Icon name={c.icon} className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-heading font-semibold text-white">{c.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 grid sm:grid-cols-3 gap-4 text-sm">
              <div className="glass rounded-xl p-5"><p className="text-teal text-xs uppercase tracking-wider">Internships</p><p className="mt-2 text-slate-300">Structured internships across engineering, product and data.</p></div>
              <div className="glass rounded-xl p-5"><p className="text-teal text-xs uppercase tracking-wider">Location</p><p className="mt-2 text-slate-300">{COMPANY.corporateOffice} — Remote & Hybrid roles available.</p></div>
              <div className="glass rounded-xl p-5"><p className="text-teal text-xs uppercase tracking-wider">Equal Opportunity</p><p className="mt-2 text-slate-300">We are an equal-opportunity employer committed to a diverse, inclusive workplace.</p></div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Benefits */}
      <section className="relative py-16 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <SectionHeading overline="Benefits" title="Employee benefits" />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CAREER_BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={(i % 3) * 0.06}>
                <div className="glass rounded-2xl p-6 h-full hover:border-teal/40 transition-colors">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-teal/10 border border-teal/30 text-teal">
                    <Icon name={b.icon} className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-heading font-semibold text-white">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Hiring process + Life at Vardhix */}
      <section className="relative py-16 border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHeading overline="Hiring Process" title="How we hire" />
            <div className="mt-10 relative">
              <div className="absolute left-4 top-0 bottom-0 w-px bg-gradient-to-b from-teal/60 via-white/10 to-transparent" />
              <div className="space-y-6">
                {HIRING_PROCESS.map((h, i) => (
                  <Reveal key={h.step} delay={i * 0.06}>
                    <div className="relative pl-12">
                      <span className="absolute left-4 top-1.5 h-3 w-3 -translate-x-1/2 rounded-full bg-teal shadow-[0_0_12px_#00D4AA] ring-4 ring-navy" />
                      <h4 className="font-heading font-semibold text-white">{i + 1}. {h.step}</h4>
                      <p className="mt-1 text-sm text-slate-400">{h.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
          <div>
            <SectionHeading overline="Life at Vardhix" title="What it's like to work here" />
            <div className="mt-10 grid gap-3">
              {LIFE_AT_VARDHIX.map((l, i) => (
                <Reveal key={l} delay={i * 0.05}>
                  <div className="flex items-start gap-3 glass rounded-xl px-5 py-4">
                    <Icon name="Check" className="h-5 w-5 text-teal shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-200">{l}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply" className="relative py-20 border-t border-white/10">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <SectionHeading center overline="Apply" title="Submit your application" sub="Share your details and links. We review every application." />
          <Reveal delay={0.1}>
            <form onSubmit={onSubmit} className="mt-10 glass rounded-3xl p-8 space-y-5" data-testid="career-form">
              <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name *"><Input data-testid="career-name" value={form.full_name} onChange={set("full_name")} placeholder="Your name" className="bg-white/5 border-white/10" /></Field>
                <Field label="Email *"><Input data-testid="career-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="bg-white/5 border-white/10" /></Field>
                <Field label="Phone"><Input data-testid="career-phone" value={form.phone} onChange={set("phone")} placeholder="+91" className="bg-white/5 border-white/10" /></Field>
                <Field label="Experience"><Input data-testid="career-exp" value={form.experience} onChange={set("experience")} placeholder="e.g. 3 years" className="bg-white/5 border-white/10" /></Field>
              </div>
              <Field label="Preferred Role *">
                <Select value={form.role} onValueChange={set("role")}>
                  <SelectTrigger className="bg-white/5 border-white/10" data-testid="career-role"><SelectValue placeholder="Select a role" /></SelectTrigger>
                  <SelectContent className="bg-matte border-white/10 text-white">
                    {CAREER_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="LinkedIn"><Input data-testid="career-linkedin" value={form.linkedin} onChange={set("linkedin")} placeholder="LinkedIn URL" className="bg-white/5 border-white/10" /></Field>
                <Field label="Portfolio / Resume URL"><Input data-testid="career-portfolio" value={form.portfolio} onChange={set("portfolio")} placeholder="Link to resume or portfolio" className="bg-white/5 border-white/10" /></Field>
              </div>
              <Field label="Message"><Textarea data-testid="career-message" value={form.message} onChange={set("message")} placeholder="Tell us about yourself" rows={4} className="bg-white/5 border-white/10" /></Field>
              <PillButton type="submit" onClick={undefined} testid="career-submit" icon="ArrowRight">
                {loading ? "Submitting..." : "Submit Application"}
              </PillButton>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-slate-300 text-sm">{label}</Label>
      {children}
    </div>
  );
}
